// Shared gallery metadata and helpers for carousels
// Centralizes image lists, marketing captions, and SEO alt text.

export type GalleryItem = { src: string; feature: string };

// 1) Source images (in public/)
export const rawFiles: string[] = [
  'AI_sWOT_Analysis.png',
  'Analyst_Forecasts_history_And_Company_reported_together.png',
  'Recent_Earnings_Report_ANd_Conf_call.png',
  'Your_Own_Projection_Model.png',
  'business_model_with_different_model_next_year_price_targets.png',
  'cashflow_breakdown.png',
  'company_performance.png',
  'current_balancesheet.png',
  'heatmap_for_grading_financials.png',
  'income_statement_sankey.png',
  'stock_comparison_with_heatmap.png',
];

// 2) Optional priority ordering (put hero slides first if needed)
export const prioritized: string[] = ['income_statement_sankey.png'];

// 3) Human-friendly label generator from filename
export const toFeature = (name: string): string => {
  const base = name
    .replace(/\.[^.]+$/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return base.replace(/\b([a-z])(\w*)/gi, (_m, a: string, b: string) => a.toUpperCase() + b.toLowerCase());
};

// 4) Ordered file list
export const orderedFiles: string[] = [
  ...prioritized,
  ...rawFiles.filter((f) => !prioritized.includes(f)),
];

// 5) Build gallery items
export const buildGallery = (): GalleryItem[] =>
  orderedFiles.map((f) => ({ src: `/${f}`, feature: toFeature(f) }));

// 6) SEO alt text map
const altMap: Record<string, string> = {
  'AI_sWOT_Analysis.png':
    'AI-assisted SWOT analysis interface with editable strengths, weaknesses, opportunities, and threats',
  'Analyst_Forecasts_history_And_Company_reported_together.png':
    'Table comparing analyst forecasts with company-reported results showing beat/miss history and forward P/E',
  'Recent_Earnings_Report_ANd_Conf_call.png':
    'Quarterly earnings report summary panel with financial performance, segments, outlook, and highlights',
  'Your_Own_Projection_Model.png':
    'Interactive bull, base, and bear scenario projections with revenue, margins, EPS, and price ranges',
  'business_model_with_different_model_next_year_price_targets.png':
    'Business model summary with blended, consensus, intrinsic (EPS×PE), and DCF next-year price targets',
  'cashflow_breakdown.png':
    'Cash flow Sankey illustrating operating cash flow, CapEx, free cash flow, and net change in cash',
  'company_performance.png':
    'Company performance view with revenue growth, margins, net income bars and share price overlay',
  'current_balancesheet.png':
    'Balance sheet composition with total assets, liabilities, and equity visualized for the current year',
  'heatmap_for_grading_financials.png':
    'Income statement heatmap with color-coded trends for revenue, costs, and profitability metrics',
  'income_statement_sankey.png':
    'Income statement Sankey diagram from revenue to operating profit to net income with expense breakdowns',
  'stock_comparison_with_heatmap.png':
    'Stock comparison heatmap of valuation and growth metrics versus peers and market benchmarks',
};

// 7) Marketing captions map used for on-screen badges
const captionMap: Record<string, string> = {
  'AI_sWOT_Analysis.png': 'AI‑powered strategic snapshot',
  'Analyst_Forecasts_history_And_Company_reported_together.png': 'Analyst vs. reported truth table',
  'Recent_Earnings_Report_ANd_Conf_call.png': '2‑minute earnings and call brief',
  'Your_Own_Projection_Model.png': 'Bull/Base/Bear conviction modeling',
  'business_model_with_different_model_next_year_price_targets.png':
    'Multi‑method price targets (blended, consensus, intrinsic, DCF)',
  'cashflow_breakdown.png': 'Cash flow made visual',
  'company_performance.png': 'Fundamentals tied to price',
  'current_balancesheet.png': 'Balance sheet strength at a glance',
  'heatmap_for_grading_financials.png': 'Heatmap grading of financials',
  'income_statement_sankey.png': 'Profit path from revenue to net',
  'stock_comparison_with_heatmap.png': 'Side‑by‑side peer comparison',
};

const fileFromSrc = (src?: string): string => (src || '').split('/').pop() || '';

export const getAlt = (src: string, feature: string): string => {
  const file = fileFromSrc(src);
  return altMap[file] || `${feature} — Stock Fortress feature screenshot`;
};

export const getCaption = (src: string, feature: string): string => {
  const file = fileFromSrc(src);
  return captionMap[file] || feature || 'Insight';
};
